import React from 'react';
import { motion } from 'framer-motion';
import { ClassComponentRegistry, sortByOrder, EnhancedCharacter } from '../../utils/ClassComponentRegistry';
import CombatStatusSection from './CombatStatusSection';
import AttackReferenceSection from './AttackReferenceSection';
import DomainCardReferenceSection from './DomainCardReferenceSection';
import ConfigurationPanel from './ConfigurationPanel';
import './CombatTracker.css';

interface CombatTrackerProps {
  enhancedCharacter: EnhancedCharacter;
}

/**
 * CombatTracker - Main combat interface container
 *
 * Refactored to use new section components with clear information hierarchy:
 * - PRIMARY: Combat Status (HP, Armor, Hope, Stress, Evasion, Thresholds) (left column on desktop)
 * - SECONDARY: Attack Reference, Class Features (right column on desktop)
 * - TERTIARY: Configuration Panel (full width bottom)
 *
 * Responsive:
 * - Mobile: Single column, stacked vertically
 * - Tablet: Hybrid layout
 * - Desktop: Two-column grid
 */
const CombatTracker: React.FC<CombatTrackerProps> = ({ enhancedCharacter }) => {
  if (!enhancedCharacter.character) {
    return (
      <div className="combat-tracker">
        <div className="combat-tracker__loading">
          Loading character...
        </div>
      </div>
    );
  }

  const character = enhancedCharacter.character;

  // Get class-specific combat components dynamically
  const classSpecificCombatComponents = character.class
    ? sortByOrder(ClassComponentRegistry.getCombatTrackerComponents(character.class))
    : [];

  return (
    <div className="combat-tracker">
      <div className="combat-tracker__layout">
        {/* LEFT COLUMN (Desktop) / Stacked First (Mobile) */}
        <div className="combat-tracker__left-column">
          {/* PRIMARY: Combat Status (HP, Armor, Hope, Stress, Evasion, Thresholds) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <CombatStatusSection enhancedCharacter={enhancedCharacter} />
          </motion.div>
        </div>

        {/* RIGHT COLUMN (Desktop) / Stacked Second (Mobile) */}
        <div className="combat-tracker__right-column">
          {/* SECONDARY: Attack Reference */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            <AttackReferenceSection enhancedCharacter={enhancedCharacter} />
          </motion.div>

          {/* SECONDARY: Domain Card Reference */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.3 }}
          >
            <DomainCardReferenceSection enhancedCharacter={enhancedCharacter} />
          </motion.div>

          {/* SECONDARY: Dynamic Class-Specific Combat Components */}
          {classSpecificCombatComponents.map((combatComponent, index) => {
            const Component = combatComponent.component;
            return (
              <motion.div
                key={`class-combat-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1, duration: 0.3 }}
              >
                <Component enhancedCharacter={enhancedCharacter} />
              </motion.div>
            );
          })}
        </div>

        {/* FULL WIDTH: Configuration Panel (TERTIARY) */}
        <motion.div
          className="combat-tracker__configuration"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.3 }}
        >
          <ConfigurationPanel
            enhancedCharacter={enhancedCharacter}
            showDebugStats={process.env.NODE_ENV === 'development'}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default CombatTracker;